import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Box, Typography, Button, Select, MenuItem, FormControl, Link, Grid, CircularProgress, Alert } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import CustomCheckbox from '../../../common/components/CustomCheckbox/CustomCheckbox';
import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { useNavItems } from '../../../hooks';
import { useModuleOperators } from '../../../hooks/useModuleOperators';
import NotificationSnackbar from '../../../common/components/Snackbar/Snackbar';
import { useItemsKgReport } from './hooks/useItemsKgReport';
import { ItemsKgTable } from './components';
import { 
  MainContent,
  ReportContainer,
  SectionContainer,
  SectionTitle,
  CheckboxGroup,
  ButtonContainer,
  ActionButton,
  FilterSection,
  SelectContainer,
  StyledSegmentedControl,
  StyledSegmentButton,
  ClearResultsButton,
  ResetButton,
  ExportButton
} from './ItemsKgReport.styled';

interface DirectionOption {
  id: string;
  label: string;
}

// Utility function to compare year-quarter combinations
const compareYearQuarter = (year1: string, quarter1: string, year2: string, quarter2: string): number => {
  const y1 = parseInt(year1);
  const y2 = parseInt(year2);
  const q1 = parseInt(quarter1.replace('Q', ''));
  const q2 = parseInt(quarter2.replace('Q', ''));
  
  if (y1 !== y2) {
    return y1 - y2; // Compare years first
  }
  return q1 - q2; // If years are equal, compare quarters
};

function ItemsKgReport() {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const navigate = useNavigate();
  const navItems = useNavItems();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [ppoValue, setPpoValue] = useState('ALL');
  const [ppoFilter, setPpoFilter] = useState('Inbound');
  const [fromYear, setFromYear] = useState('');
  const [fromQuarter, setFromQuarter] = useState('');
  const [toYear, setToYear] = useState('');
  const [toQuarter, setToQuarter] = useState('');
  const [onlyCompactItems, setOnlyCompactItems] = useState<boolean>(false);

  // Temporary state for SectionBar filters (only applied on export)
  const [tempSectionBarFilters, setTempSectionBarFilters] = useState({
    fromYear: '',
    fromQuarter: '',
    toYear: '',
    toQuarter: '',
    direction: 'Inbound',
    compactOnly: false,
  });

  // Add state for HTML results
  const [htmlResults, setHtmlResults] = useState<any | null>(null);
    console.log(htmlResults);
    
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error' as 'error' | 'warning' | 'info' | 'success'
  });

  // Items-kg Report hook
  const {
    mutate: generateReport,
    isPending: isGeneratingReport,
    error: reportError,
    reset: resetHookError
  } = useItemsKgReport();

  // Get effective operators and cascading logic for e55 module
  const {
    operators,
    availableYears,
    operatorsForYearRange,
    yearsForOperator,
    relationsData: allRelationsData,
    isPpoLoading,
    ppoError,
  } = useModuleOperators('e55', { fromYear, toYear, selectedPpo: ppoValue === 'ALL' ? undefined : ppoValue });

  // Map the target DOs to PPO options with cascading logic
  const ppoOptions = React.useMemo(() => {
    const options: { id: string; label: string; hasLicense?: boolean }[] = [];
    const targetDOsToUse = (fromYear || toYear) ? operatorsForYearRange : operators;
    targetDOsToUse.forEach((targetDO) => {
      options.push({ id: targetDO, label: targetDO, hasLicense: true });
    });
    return options;
  }, [operators, operatorsForYearRange, fromYear, toYear]);

  // Generate year-quarter options with cascading logic
  const yearQuarterOptions = React.useMemo(() => {
    const options: { value: string; label: string; year: string; quarter: string }[] = [];
    const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
    const yearsToShow = ppoValue && ppoValue !== 'ALL' ? yearsForOperator : availableYears;
    yearsToShow.forEach((year) => {
      quarters.forEach(quarter => {
        const value = `${year}-${quarter}`;
        const label = `${year} ${quarter}`;
        options.push({ value, label, year: year.toString(), quarter });
      });
    });
    return options;
  }, [availableYears, yearsForOperator, ppoValue]);

  // Get current from and to values for display
  const getFromValue = () => {
    if (fromYear && fromQuarter) {
      return `${fromYear}-${fromQuarter}`;
    }
    return '';
  };

  const getToValue = () => {
    if (toYear && toQuarter) {
      return `${toYear}-${toQuarter}`;
    }
    return '';
  };

  // Handle from dropdown change
  const handleFromChange = (value: string) => {
    if (value) {
      const option = yearQuarterOptions.find(opt => opt.value === value);
      if (option) {
        // Check if selected from date is later than current to date
        if (toYear && toQuarter) {
          const comparison = compareYearQuarter(option.year, option.quarter, toYear, toQuarter);
          if (comparison > 0) {
            showSnackbar('From date cannot be later than To date. Please select a valid date range.', 'warning');
            return; // Don't update the state if invalid
          }
        }
        setFromYear(option.year);
        setFromQuarter(option.quarter);
      }
    } else {
      setFromYear('');
      setFromQuarter('');
    }
  };

  // Handle to dropdown change
  const handleToChange = (value: string) => {
    if (value) {
      const option = yearQuarterOptions.find(opt => opt.value === value);
      if (option) {
        // Check if selected to date is earlier than current from date
        if (fromYear && fromQuarter) {
          const comparison = compareYearQuarter(fromYear, fromQuarter, option.year, option.quarter);
          if (comparison > 0) {
            showSnackbar('To date cannot be earlier than From date. Please select a valid date range.', 'warning');
            return; // Don't update the state if invalid
          }
        }
        
        // Only clear DO selection if the currently selected DO is not available for the new year range
        if (toYear !== option.year && ppoValue && ppoValue !== 'ALL') {
          // Check if the current DO is available for the new year range
          const relationsForCurrentDO = allRelationsData?.byTargetDO?.[ppoValue];
          if (!relationsForCurrentDO) {
            setPpoValue('ALL');
            setToYear(option.year);
            setToQuarter(option.quarter);
            return;
          }
          
          // Get years where this DO has e55 module enabled
          const availableYears = relationsForCurrentDO
            .filter(relation => 
              relation.modules.some(module => 
                module.name === 'e55' && module.enabled === true
              )
            )
            .map(relation => relation.year)
            .sort((a, b) => a - b);
          
          if (availableYears.length === 0) {
            setPpoValue('ALL');
            setToYear(option.year);
            setToQuarter(option.quarter);
            return;
          }
          
          const minYear = availableYears[0];
          const maxYear = availableYears[availableYears.length - 1];
          const newToYearNum = parseInt(option.year);
          
          // Check if the new to year is within the DO's range
          let shouldClearDO = false;
          
          if (fromYear) {
            // Both from and to are selected - check if the range overlaps with DO's range
            const fromYearNum = parseInt(fromYear);
            shouldClearDO = !(maxYear >= fromYearNum && minYear <= newToYearNum);
          } else {
            // Only to year is selected - check if DO has data up to that year
            shouldClearDO = minYear > newToYearNum;
          }
          
          if (shouldClearDO) {
            setPpoValue('ALL');
          }
        }
        
        setToYear(option.year);
        setToQuarter(option.quarter);
      }
    } else {
      setToYear('');
      setToQuarter('');
    }
  };

  // Direction filter options
  const directionOptions: DirectionOption[] = [
    { id: 'Inbound', label: 'Inbound' },
    { id: 'Outbound', label: 'Outbound' },
  ];

  const handleSidebarToggle = () => {
    setSidebarOpen(prev => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/e55');
  };

  const handleDirectionChange = (id: string) => {
    setPpoFilter(id);
  };

  // Handle DO selection change — years are independent of the selected DO
  const handleDOChange = (value: string) => {
    setPpoValue(value);
  };

  // Handle year change with cascading behavior
  const handleFromChangeWithCascading = (value: string) => {
    if (value) {
      const option = yearQuarterOptions.find(opt => opt.value === value);
      if (option) {
        // Check if selected from date is later than current to date
        if (toYear && toQuarter) {
          const comparison = compareYearQuarter(option.year, option.quarter, toYear, toQuarter);
          if (comparison > 0) {
            showSnackbar('From date cannot be later than To date. Please select a valid date range.', 'warning');
            return; // Don't update the state if invalid
          }
        }
        
        // Check if the currently selected DO is available for the new year range
        if (ppoValue && ppoValue !== 'ALL') {
          const relationsForCurrentDO = allRelationsData?.byTargetDO?.[ppoValue];
          if (!relationsForCurrentDO) {
            setPpoValue('ALL');
            setFromYear(option.year);
            setFromQuarter(option.quarter);
            return;
          }
          
          // Get years where this DO has e55 module enabled
          const availableYears = relationsForCurrentDO
            .filter(relation => 
              relation.modules.some(module => 
                module.name === 'e55' && module.enabled === true
              )
            )
            .map(relation => relation.year)
            .sort((a, b) => a - b);
          
          if (availableYears.length === 0) {
            setPpoValue('ALL');
            setFromYear(option.year);
            setFromQuarter(option.quarter);
            return;
          }
          
          const minYear = availableYears[0];
          const maxYear = availableYears[availableYears.length - 1];
          const newFromYearNum = parseInt(option.year);
          
          // Check if the new from year is within the DO's range
          let shouldClearDO = false;
          
          if (toYear) {
            // Both from and to are selected - check if the range overlaps with DO's range
            const toYearNum = parseInt(toYear);
            shouldClearDO = !(maxYear >= newFromYearNum && minYear <= toYearNum);
          } else {
            // Only from year is selected - check if DO has data from that year onwards
            shouldClearDO = maxYear < newFromYearNum;
          }
          
          if (shouldClearDO) {
            setPpoValue('ALL');
          }
        }
        
        setFromYear(option.year);
        setFromQuarter(option.quarter);
      }
    } else {
      setFromYear('');
      setFromQuarter('');
    }
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const showSnackbar = (message: string, severity: 'error' | 'warning' | 'info' | 'success' = 'error') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleReset = () => {
    setPpoValue('');
    setPpoFilter('Inbound');
    setFromYear('');
    setFromQuarter('');
    setToYear('');
    setToQuarter('');
    setOnlyCompactItems(false);
  };

  const resetReportError = () => {
    resetHookError();
  };

  // Function to apply current filters to SectionBar (URL params)
  const applyFiltersToSectionBar = () => {
    const newParams = new URLSearchParams();

    // Map PPO filter
    if (ppoValue) {
      newParams.append('ppo', ppoValue);
    } else {
      newParams.append('ppo', 'ALL');
    }

    if (fromYear && fromQuarter) {
      newParams.append('fromYear', fromYear);
      newParams.append('fromQuarter', fromQuarter);
      newParams.append('fromPeriod', `${fromYear}-${fromQuarter}`);
    }

    if (toYear && toQuarter) {
      newParams.append('toYear', toYear);
      newParams.append('toQuarter', toQuarter);
      newParams.append('toPeriod', `${toYear}-${toQuarter}`);
    }

    if (onlyCompactItems) {
      newParams.append('compactOnly', 'true');
    }

    setSearchParams(newParams);
  };

  const handleExport = (format: 'HTML' | 'XLS', isFromSectionBar = false) => {
    // Reset any previous errors
    resetReportError();

    // Determine which state to use based on source
    let currentFromYear = fromYear;
    let currentFromQuarter = fromQuarter;
    let currentToYear = toYear;
    let currentToQuarter = toQuarter;
    let currentDirection = ppoFilter;
    let currentCompactOnly = onlyCompactItems;

    // If called from SectionBar, use temporary state and apply it to main state
    if (isFromSectionBar && htmlResults) {
      currentFromYear = tempSectionBarFilters.fromYear || fromYear;
      currentFromQuarter = tempSectionBarFilters.fromQuarter || fromQuarter;
      currentToYear = tempSectionBarFilters.toYear || toYear;
      currentToQuarter = tempSectionBarFilters.toQuarter || toQuarter;
      currentDirection = tempSectionBarFilters.direction;
      currentCompactOnly = tempSectionBarFilters.compactOnly;

      // Apply temporary filters to main state
      if (tempSectionBarFilters.fromYear) setFromYear(tempSectionBarFilters.fromYear);
      if (tempSectionBarFilters.fromQuarter) setFromQuarter(tempSectionBarFilters.fromQuarter);
      if (tempSectionBarFilters.toYear) setToYear(tempSectionBarFilters.toYear);
      if (tempSectionBarFilters.toQuarter) setToQuarter(tempSectionBarFilters.toQuarter);
      setPpoFilter(tempSectionBarFilters.direction);
      setOnlyCompactItems(tempSectionBarFilters.compactOnly);
    }

    // Validate required fields
    if (!ppoValue) {
      showSnackbar('Please select a PPO');
      return;
    }

    if (!currentFromYear || !currentFromQuarter) {
      showSnackbar('Please select a From period (Year and Quarter)');
      return;
    }

    if (!currentToYear || !currentToQuarter) {
      showSnackbar('Please select a To period (Year and Quarter)');
      return;
    }

    // Call the API with the hook
    generateReport({
      designatedOperator: ppoValue === 'ALL' ? 'All' : ppoValue,
      direction: currentDirection as 'Inbound' | 'Outbound',
      fromYear: parseInt(currentFromYear),
      fromQuarter: parseInt(currentFromQuarter.replace('Q', '')),
      toYear: parseInt(currentToYear),
      toQuarter: parseInt(currentToQuarter.replace('Q', '')),
      unex: currentCompactOnly,
      formatType: format.toLowerCase() as 'html' | 'xls'
    }, {
      onSuccess: (data) => {
        if (format === 'HTML') {
          // For HTML, set the results and apply filters
          setHtmlResults(data);
          applyFiltersToSectionBar();
          showSnackbar('HTML report generated successfully', 'success');
        } else {
          // For XLS, file download is handled in the hook
          showSnackbar(`${format} report downloaded successfully`, 'success');
        }
      },
      onError: async (error: Error) => {
        console.error('Error generating report:', error);
        
        // Handle NOT_FOUND errors for XLS exports
        if (format !== 'HTML') {
          let errorMessage = 'An error occurred while generating the report';
          let errorStatus: string | undefined;
          
          // Check if it's an axios error
          if (axios.isAxiosError(error)) {
            // For blob responses, the error data might be a Blob containing JSON
            if (error.response?.data instanceof Blob) {
              try {
                const text = await error.response.data.text();
                const errorData = JSON.parse(text);
                errorMessage = errorData.message || errorMessage;
                errorStatus = errorData.status;
              } catch (parseError) {
                // If parsing fails, use default message
                console.error('Failed to parse error response:', parseError);
              }
            } else if (error.response?.data && typeof error.response.data === 'object') {
              // For non-blob responses, check the data directly
              const errorData = error.response.data as { message?: string; status?: string };
              errorMessage = errorData.message || errorMessage;
              errorStatus = errorData.status;
            }
            
            // Check status code as fallback
            if (!errorStatus && error.response?.status === 404) {
              errorStatus = 'NOT_FOUND';
            }
          }
          
          // Show snackbar with appropriate message
          if (errorStatus === 'NOT_FOUND' || errorMessage.includes('No Items-kg reports available') || errorMessage.includes('No Items-kg')) {
            showSnackbar(
              errorMessage || 'No Items-kg reports available for the selected criteria',
              'warning'
            );
          } else {
            showSnackbar(errorMessage, 'error');
          }
        } else {
          // For HTML format, show error snackbar
          showSnackbar(
            error instanceof Error ? error.message : 'Failed to generate report. Please try again.',
            'error'
          );
        }
      }
    });
  };

  // Initialize temporary SectionBar filters when HTML results are first generated
  useEffect(() => {
    if (htmlResults) {
      setTempSectionBarFilters({
        fromYear,
        fromQuarter,
        toYear,
        toQuarter,
        direction: ppoFilter,
        compactOnly: onlyCompactItems,
      });
    }
  }, [htmlResults && !tempSectionBarFilters.fromYear]); // Only run when htmlResults is first set

  // Sync URL params with page state when SectionBar filters change (only when results exist)
  useEffect(() => {
    const ppoParam = searchParams.get('ppo');

    // Only sync if we have HTML results (filters are active)
    if (htmlResults) {
      if (ppoParam && ppoParam !== ppoValue) {
        setPpoValue(ppoParam);
      }
    }
  }, [searchParams, htmlResults, ppoValue]);

  // Memoize clearResults callback
  const clearResults = useCallback(() => {
    setHtmlResults(null);
    // Reset temporary SectionBar filters
    setTempSectionBarFilters({
      fromYear: '',
      fromQuarter: '',
      toYear: '',
      toQuarter: '',
      direction: 'Inbound',
      compactOnly: false,
    });
    // Clear URL parameters when results are cleared
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  // Helper functions for temporary SectionBar filters
  const getTempFromValue = () => {
    if (tempSectionBarFilters.fromYear && tempSectionBarFilters.fromQuarter) {
      return `${tempSectionBarFilters.fromYear}-${tempSectionBarFilters.fromQuarter}`;
    }
    return getFromValue(); // Fallback to main state
  };

  const getTempToValue = () => {
    if (tempSectionBarFilters.toYear && tempSectionBarFilters.toQuarter) {
      return `${tempSectionBarFilters.toYear}-${tempSectionBarFilters.toQuarter}`;
    }
    return getToValue(); // Fallback to main state
  };

  // Create custom filters object for SectionBar
  const customFiltersForSectionBar = useMemo(() => {
    if (!htmlResults) return undefined;

    return {
      fromPeriod: getTempFromValue(),
      toPeriod: getTempToValue(),
      direction: tempSectionBarFilters.direction || ppoFilter,
      compactOnly: tempSectionBarFilters.compactOnly !== undefined ? tempSectionBarFilters.compactOnly : onlyCompactItems,
      onFromPeriodChange: (period: string) => {
        if (period) {
          const option = yearQuarterOptions.find(opt => opt.value === period);
          if (option) {
            setTempSectionBarFilters(prev => ({
              ...prev,
              fromYear: option.year,
              fromQuarter: option.quarter,
            }));
            
            // Update URL parameters
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('fromYear');
            newParams.delete('fromQuarter');
            newParams.delete('fromPeriod');
            newParams.append('fromYear', option.year);
            newParams.append('fromQuarter', option.quarter);
            newParams.append('fromPeriod', period);
            setSearchParams(newParams);
          }
        } else {
          setTempSectionBarFilters(prev => ({
            ...prev,
            fromYear: '',
            fromQuarter: '',
          }));
          
          // Update URL parameters to clear from period
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('fromYear');
          newParams.delete('fromQuarter');
          newParams.delete('fromPeriod');
          setSearchParams(newParams);
        }
      },
      onToPeriodChange: (period: string) => {
        if (period) {
          const option = yearQuarterOptions.find(opt => opt.value === period);
          if (option) {
            setTempSectionBarFilters(prev => ({
              ...prev,
              toYear: option.year,
              toQuarter: option.quarter,
            }));
            
            // Update URL parameters
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('toYear');
            newParams.delete('toQuarter');
            newParams.delete('toPeriod');
            newParams.append('toYear', option.year);
            newParams.append('toQuarter', option.quarter);
            newParams.append('toPeriod', period);
            setSearchParams(newParams);
          }
        } else {
          setTempSectionBarFilters(prev => ({
            ...prev,
            toYear: '',
            toQuarter: '',
          }));
          
          // Update URL parameters to clear to period
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('toYear');
          newParams.delete('toQuarter');
          newParams.delete('toPeriod');
          setSearchParams(newParams);
        }
      },
      onDirectionChange: (direction: string) => {
        setTempSectionBarFilters(prev => ({
          ...prev,
          direction,
        }));
        
        // Update URL parameters
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('type');
        newParams.append('type', direction.toLowerCase());
        setSearchParams(newParams);
      },
      onCompactOnlyChange: (value: boolean) => {
        setTempSectionBarFilters(prev => ({
          ...prev,
          compactOnly: value,
        }));
      },
      onExport: (format: 'HTML' | 'XLS' | 'PDF') => {
        if (format === 'PDF') return; // PDF not supported
        handleExport(format, true); // Pass true to indicate this is from SectionBar
      },
      isExporting: isGeneratingReport,
    };
  }, [
    htmlResults,
    tempSectionBarFilters,
    fromYear,
    fromQuarter,
    toYear,
    toQuarter,
    ppoFilter,
    onlyCompactItems,
    yearQuarterOptions,
    searchParams,
    setSearchParams,
    handleExport,
    isGeneratingReport,
  ]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid='items-kg-report-container'>
      <Grid container>
        {/* First Row - SectionBar */}
        <Grid item xs={12}>
          <SectionBar
            title='Items-kg Report'
            subtitle="e55 Weights"
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={sidebarOpen}
            onSubtitleClick={handleSubtitleClick}
            showFilters={{
              ppo: !!htmlResults,
              customItemsKg: !!htmlResults,
            }}
            customFilters={customFiltersForSectionBar}
            data-testid="section-bar"
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }}>
              <Sidebar isOpen={sidebarOpen} onToggle={handleSidebarToggle} navItems={navItems} data-testid="items-kg-report-sidebar" />
            </Box>
          )}

          {/* Main Content */}
          <MainContent sidebarOpen={sidebarOpen} data-testid='items-kg-report-main-content'>
            <ReportContainer data-testid="items-kg-report-content">
              {/* Header */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 3 
              }} data-testid="items-kg-report-header">
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#063142' }} data-testid="items-kg-report-title">
                  Items-kg Report
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }} data-testid="items-kg-report-header-actions">
                  {htmlResults && (
                    <ClearResultsButton 
                      variant="outlined" 
                      onClick={clearResults}
                      data-testid="items-kg-report-clear-results-btn"
                    >
                      Clear Results
                    </ClearResultsButton>
                  )}
                </Box>
              </Box>

              {/* Error Alert */}
              {reportError && (
                <Alert 
                  severity="error" 
                  sx={{ mb: 2 }}
                  onClose={() => resetReportError()}
                  data-testid="items-kg-report-error-alert"
                >
                  {reportError.message || 'An error occurred while generating the report'}
                </Alert>
              )}

              {/* Main Filter Section - Hide when HTML results exist */}
              {!htmlResults && (
                <>
                  {/* PPO Section */}
                  <FilterSection data-testid="items-kg-report-filters">
                    {/* PPO Dropdown and Direction Filter in one row */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, mb: 2 }} data-testid="items-kg-report-ppo-direction-section">
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} data-testid="items-kg-report-ppo-section">
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                          DO
                        </Typography>
                        <SelectContainer>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={ppoValue}
                              onChange={(e) => handleDOChange(e.target.value)}
                              displayEmpty
                              sx={{ minWidth: 160 }}
                              disabled={isPpoLoading}
                              data-testid="items-kg-report-ppo-select"
                            >
                              <MenuItem value="ALL">All</MenuItem>
                              {isPpoLoading && (
                                <MenuItem value="" disabled>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} />
                                    <span>Loading...</span>
                                  </Box>
                                </MenuItem>
                              )}
                              {!isPpoLoading && !ppoError && ppoOptions.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                    <span>{option.label}</span>
                                  </Box>
                                </MenuItem>
                              ))}
                              {ppoError && (
                                <MenuItem disabled>Error loading PPOs</MenuItem>
                              )}
                            </Select>
                          </FormControl>
                        </SelectContainer>
                      </Box>
                      
                      {/* Direction Filter using Segmented Control */}
                      <StyledSegmentedControl>
                        {directionOptions.map((option) => (
                          <StyledSegmentButton
                            key={option.id}
                            active={ppoFilter === option.id}
                            onClick={() => handleDirectionChange(option.id)}
                            sx={{ 
                              color: ppoFilter === option.id ? '#ffffff' : '#00B6BA',
                              minWidth: '70px'
                            }}
                          >
                            {option.label}
                          </StyledSegmentButton>
                        ))}
                      </StyledSegmentedControl>
                    </Box>

                    {/* Divider */}
                    <Box sx={{ 
                      height: '1px', 
                      backgroundColor: '#E1E8ED', 
                      mb: 2 
                    }} />

                    {/* Date Selectors with titles */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                          From YYYY.QQ: *
                        </Typography>
                        <SelectContainer>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={getFromValue()}
                              onChange={(e) => handleFromChangeWithCascading(e.target.value)}
                              displayEmpty
                              sx={{ minWidth: 184 }}
                            >
                              <MenuItem value="">--Please Select--</MenuItem>
                              {yearQuarterOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </SelectContainer>
                      </Box>
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                          To YYYY.QQ: *
                        </Typography>
                        <SelectContainer>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={getToValue()}
                              onChange={(e) => handleToChange(e.target.value)}
                              displayEmpty
                              sx={{ minWidth: 184 }}
                            >
                              <MenuItem value="">--Please Select--</MenuItem>
                              {yearQuarterOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </SelectContainer>
                      </Box>
                    </Box>

                    {/* Divider */}
                    <Box sx={{ 
                      height: '1px', 
                      backgroundColor: '#E1E8ED', 
                      my: 2 
                    }} />

                    {/* Checkbox for UNEX compact items */}
                    <Box sx={{ padding: 2 }}>
                      <CheckboxGroup>
                        <CustomCheckbox
                          checked={onlyCompactItems}
                          onChange={(e) => setOnlyCompactItems(e.target.checked)}
                          label="Only compact items and weight report for UNEX"
                        />
                      </CheckboxGroup>
                    </Box>
                  </FilterSection>

                  {/* Action Buttons */}
                  <ButtonContainer sx={{ justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {htmlResults && (
                        <ClearResultsButton 
                          variant="outlined" 
                          onClick={clearResults}
                          size="small"
                        >
                          Clear Results
                        </ClearResultsButton>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <ResetButton 
                        variant="contained" 
                        onClick={handleReset}
                      >
                        RESET
                      </ResetButton>
                      <ExportButton 
                        variant="contained" 
                        onClick={() => handleExport('HTML')}
                        disabled={isGeneratingReport}
                      >
                        {isGeneratingReport ? <CircularProgress size={16} /> : 'HTML'}
                      </ExportButton>
                      <ExportButton 
                        variant="contained" 
                        onClick={() => handleExport('XLS')}
                        disabled={isGeneratingReport}
                      >
                        {isGeneratingReport ? <CircularProgress size={16} /> : 'XLS'}
                      </ExportButton>
                    </Box>
                  </ButtonContainer>
                </>
              )}

              {/* Results Section - Show when HTML results exist */}
              {htmlResults && (
                <Box sx={{ mt: 6 }}>
                  <Box sx={{ 
                    borderBottom: '2px solid #E1E8ED',
                    pb: 2,
                    mb: 4
                  }}>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 700, 
                      color: '#063142',
                      fontSize: '24px',
                      letterSpacing: '-0.5px'
                    }}>
                      Report Results
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: '#666',
                      mt: 0.5,
                      fontSize: '14px'
                    }}>
                      Generated Items-kg report data
                    </Typography>
                  </Box>
                  
                  {/* Items-kg Report Table */}
                  <ItemsKgTable
                    data={htmlResults.data || htmlResults}
                    isLoading={false}
                    moduleName="Items-kg Report"
                    unexEnabled={onlyCompactItems}
                    rowBackgroundColor={ppoFilter === 'Inbound' ? 'rgb(230, 245, 230)' : 'rgb(255, 240, 240)'}
                    data-testid="items-kg-results-table"
                  />
                </Box>
              )}
            </ReportContainer>
          </MainContent>
        </Grid>
      </Grid>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
        data-testid="items-kg-report-snackbar"
      />
    </Box>
  );
}

export default ItemsKgReport; 